import * as React from "react"
import { cn } from "@/lib/utils"

type InputProps = React.ComponentProps<"input">

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, maxLength, onChange, value, defaultValue, ...props }, ref) => {
    const [innerLength, setInnerLength] = React.useState(
      typeof defaultValue === "string" ? [...defaultValue].length : 0
    )

    const isControlled = typeof value !== "undefined"
    const currentLength = isControlled
      ? (typeof value === "string" ? [...value].length : 0)
      : innerLength

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) setInnerLength([...e.target.value].length)
      onChange?.(e)
    }

    const handleRemoveSpace = (e:  React.ChangeEvent<HTMLInputElement>) => {
      if (isControlled) return
      const newValue = e.target.value.replace(/\s/g, '')
      setInnerLength(newValue.length)
    }

    const baseClass = cn(
      "file:text-foreground placeholder:text-grayscale-30 h-9 w-full md:h-12 rounded-md border p-4 text-xs md:text-base shadow-xs font-medium transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-base",
      "focus-visible:border-grayscale-10 focus-visible:ring-ring/50 focus-visible:ring-[3px]",
      "aria-invalid:border-destructive aria-invalid:destructive/20 dark:aria-invalid:destructive/40",
      "aria-invalid:focus:border-destructive aria-invalid:focus:destructive/30",
      "aria-invalid:focus-visible:!border-destructive aria-invalid:focus-visible:!destructive/30",
      className
    )

    // maxLength가 없으면 - 기존 그대로 렌더
    if (typeof maxLength !== "number") {
      return (
        <input
          ref={ref}
          type={type}
          data-slot="input"
          className={baseClass}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          onInput={handleRemoveSpace}
          {...props}
        />
      )
    }

    // maxLength가 있으면 - currentLength/maxLength
    return (
      <div className={cn(
        "group/input-group bg-white relative flex min-w-128 items-center rounded-md border shadow-xs transition-[color,box-shadow]",
        "has-[:focus-visible]:border-grayscale-10 has-[:focus-visible]:ring-ring/50 has-[:focus-visible]:ring-[3px]",
        "has-[[aria-invalid=true]]:ring-destructive/20 has-[[aria-invalid=true]]:border-destructive",
        "has-[:disabled]:opacity-50 has-[:disabled]:pointer-events-none", className
      )}>
        <input
          ref={ref}
          type={type}
          data-slot="input"
          maxLength={maxLength}
          className={cn(
            "file:text-foreground placeholder:text-grayscale-30 h-12 w-full p-4 pr-2 text-base font-medium outline-none file:inline-flex file:h-7 file:border-0 file:text-sm file:font-medium disabled:cursor-not-allowed md:text-base",
            "flex-1 border-0 shadow-none rounded-none bg-transparent focus-visible:ring-0",
            className
          )}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          aria-describedby={props.id ? `${props.id}-charCount` : undefined}
          {...props}
        />
        <div
          id={props.id ? `${props.id}-charCount` : undefined}
          aria-live="polite"
          className={`pointer-events-none h-auto cursor-text flex items-center justify-center gap-2 p-4 pl-0 select-none text-xs order-last has-[>button]:mr-[-0.45rem] has-[>kbd]:mr-[-0.35rem]
           ${value ? 'text-grayscale-100' : 'text-grayscale-30'} `}
        >
          {currentLength}/{maxLength}
        </div>
      </div>
    )
  }
)

Input.displayName = "Input"

export { Input }
